import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { QrCode, FileText, Scan, TrendUp, ArrowRight, House, Eye, Heart } from "@phosphor-icons/react";

function Stat({ label, value, hint, icon, testid }) {
  return (
    <div className="card card-pad" data-testid={testid} style={{ display: "flex", flexDirection: "column", gap: 12, minHeight: 140 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span className="overline">{label}</span>
        <span style={{ color: "var(--text-2)" }}>{icon}</span>
      </div>
      <div className="font-display" style={{ fontSize: 44, fontWeight: 900, lineHeight: 1, letterSpacing: "-0.03em" }}>{value}</div>
      {hint && <div style={{ fontSize: 12, color: "var(--text-2)" }}>{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
        {[1, 2, 3, 4].map((i) => <div key={i} className="card" style={{ height: 140 }} />)}
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <div className="overline">Overview</div>
          <h1 className="font-display" style={{ fontSize: 44, fontWeight: 900, margin: "8px 0 0", letterSpacing: "-0.03em" }}>Dashboard</h1>
        </div>
      </div>

      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
        <Stat testid="stat-total-qr" label="Total QR" value={stats.total_qr} hint={`${stats.active_qr} active`} icon={<QrCode size={20} weight="duotone" />} />
        <Stat testid="stat-total-pages" label="Information Pages" value={stats.total_pages} hint={`${stats.published_pages} published`} icon={<FileText size={20} weight="duotone" />} />
        <Stat testid="stat-total-scans" label="QR Scans" value={stats.total_scans} hint={`${stats.scans_today} today`} icon={<Scan size={20} weight="duotone" />} />
        <Stat testid="stat-total-visits" label="Website Visits" value={stats.total_visits ?? 0} hint={`${stats.visits_today ?? 0} today`} icon={<Eye size={20} weight="duotone" />} />
        <Stat testid="stat-home-visits" label="Home Page Views" value={stats.home_visits ?? 0} hint="Landing page" icon={<House size={20} weight="duotone" />} />
        <Stat testid="stat-page-visits" label="Info Page Views" value={stats.page_visits ?? 0} hint="Public pages" icon={<TrendUp size={20} weight="duotone" />} />
        <Stat testid="stat-total-likes" label="Total Likes" value={stats.total_likes ?? 0} hint="Across all pages" icon={<Heart size={20} weight="duotone" />} />
        <Stat testid="stat-scans-today" label="Scans Today" value={stats.scans_today} hint="Last 24h" icon={<TrendUp size={20} weight="duotone" />} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginTop: 24 }}>
        <div className="card">
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div className="overline">Top viewed pages</div>
            </div>
            <Link to="/admin/pages" className="btn btn-ghost btn-sm" data-testid="dashboard-view-pages">
              Manage <ArrowRight size={12} />
            </Link>
          </div>
          {stats.top_pages.length === 0 ? (
            <div style={{ padding: 32, color: "var(--text-2)", fontSize: 14 }}>No pages yet.</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr><th>Title</th><th style={{ textAlign: "right" }}>Views</th><th style={{ textAlign: "right" }}>Scans</th><th style={{ textAlign: "right" }}>Likes</th></tr>
              </thead>
              <tbody>
                {stats.top_pages.map((p) => (
                  <tr key={p.id} data-testid={`top-page-row-${p.id}`}>
                    <td style={{ fontWeight: 600 }}>{p.title}<div style={{ fontSize: 11, color: "var(--text-2)" }}>/{p.slug}</div></td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{p.views || 0}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{p.scans || 0}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{p.likes || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
            <div className="overline">Recent scans</div>
          </div>
          {stats.recent_scans.length === 0 ? (
            <div style={{ padding: 32, color: "var(--text-2)", fontSize: 14 }}>No scans yet. Share a QR code!</div>
          ) : (
            <div>
              {stats.recent_scans.map((s) => (
                <div key={s.id} style={{ padding: "12px 20px", borderBottom: "1px solid var(--border)", fontSize: 13 }}>
                  <div style={{ fontWeight: 600 }}>{s.qr_code}</div>
                  <div style={{ color: "var(--text-2)" }}>{s.device} · {new Date(s.scanned_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
